/**
 * Type definitions for Supervisors.
 * These interfaces, aliases, or model helpers describe the shared data contract used
 * by the surrounding feature or UI layer.
 */
import { AddressDetails } from '../../shared/models/address.types';
import { ContactDetails } from '../../shared/models/contact.types';
import { DEFAULT_GUARANTOR_CONTACT, GuarantorContact } from '../agents/agents.types';

export type SupervisorStatus = 'active' | 'inactive';
export type SupervisorApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface SupervisorProfile {
  profile: string;
  code: string;
  assignedAgency?: { agencyId?: string; agencyName?: string; city?: string; area?: string };
  guarantorContact1: GuarantorContact;
  guarantorContact2: GuarantorContact;
}

export interface SupervisorAgencyAssignment {
  id: string;
  name: string;
  city?: string;
  area?: string;
}

export interface SupervisorModel {
  id: string;
  name: string;
  email: string;
  contact: ContactDetails;
  phoneDisplay: string;
  assignedAgencies: SupervisorAgencyAssignment[];
  agencyName: string;
  address: AddressDetails;
  city: string;
  status: SupervisorStatus;
  createdAt: string;
  supervisorProfile: SupervisorProfile;
}

export interface SupervisorApprovalRequestModel {
  id: string;
  name: string;
  email: string;
  contact: ContactDetails;
  requestedAgency?: SupervisorAgencyAssignment | null;
  requestDate: string;
  approvalStatus: SupervisorApprovalStatus;
}

export interface SupervisorUpsertPayload {
  firstName?: string;
  lastName?: string;
  fullName: string;
  email: string;
  password?: string;
  preferredLanguage?: string;
  employeeCode?: string;
  roleId?: string;
  accountStatus?: string;
  contact: ContactDetails;
  address: AddressDetails;
  assignedSupervisorId?: string | null;
  assignedAgencyId?: string | null;
  assignedPosIds?: string[];
  profile: {
    supervisor: SupervisorProfile;
  };
}

export interface SupervisorRecord extends SupervisorModel {}

export interface SupervisorApprovalRequestRecord extends SupervisorApprovalRequestModel {}

export function toSupervisorRecord(id: string, payload: SupervisorUpsertPayload, current?: SupervisorRecord): SupervisorRecord {
  const phoneDisplay = formatPhoneDisplay(payload.contact.phone);
  const assignedAgency = payload.profile.supervisor.assignedAgency;
  const assignedAgencies: SupervisorAgencyAssignment[] = assignedAgency?.agencyId
    ? [{ id: assignedAgency.agencyId, name: assignedAgency.agencyName ?? '', city: assignedAgency.city, area: assignedAgency.area }]
    : payload.assignedAgencyId
      ? [{ id: payload.assignedAgencyId, name: '' }]
      : [];

  return {
    id,
    name: payload.fullName || `${payload.firstName ?? ''} ${payload.lastName ?? ''}`.trim(),
    email: payload.email,
    contact: payload.contact,
    phoneDisplay,
    assignedAgencies,
    agencyName: assignedAgencies[0]?.name ?? '',
    address: payload.address,
    city: payload.address.locality,
    status: 'active',
    createdAt: current?.createdAt ?? new Date().toISOString(),
    supervisorProfile: {
      profile: payload.profile.supervisor.profile,
      code: payload.profile.supervisor.code,
      guarantorContact1: {
        ...DEFAULT_GUARANTOR_CONTACT,
        ...(payload.profile.supervisor.guarantorContact1 ?? {})
      },
      guarantorContact2: {
        ...DEFAULT_GUARANTOR_CONTACT,
        ...(payload.profile.supervisor.guarantorContact2 ?? {})
      }
    }
  };
}

export function formatPhoneDisplay(phone: ContactDetails['phone']): string {
  const dialCode = phone.dialCode ? `+${phone.dialCode}` : '';
  return [dialCode, phone.number].filter(Boolean).join(' ').trim();
}
